/* eslint-disable @typescript-eslint/no-use-before-define */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import styled from 'styled-components';
import errorReporter from '@app/errorReporter';
import { INotificationSettings } from '@bb/domain/dist/types';
import { AuthContextType } from 'contexts';
import { LocationsDTO } from '@bb/domain/dist/types/dto/OrgController/locations';
import { Alert, message, Spin } from 'components';
import { isEqual } from 'lodash';
import { useBreakpoint } from 'hooks';
import {
  getNotificationSettings,
  getNotificationSettingsByUnsubscribeId,
  saveNotificationSettings,
  saveNotificationSettingsByUnsubscribeId,
} from 'api/NotificationController';
import { locations as getLocations } from 'api/OrgController';
import ShowingUpdatesCard from './ShowingUpdatesCard';
import ChatMessagesCard from './ChatMessagesCard';
import NotificationsCard from './NotificationsCard';
import ActionBar from './ActionBar';

const DEFAULT_NOTIFICATIONS: INotificationSettings = {
  instant: {
    exclusive: false,
    mls: false,
    assignment: false,
    comingSoon: false,
    pricechange: false,
    newConstruction: false,
  },
  dailyOpenHouse: false,
  dailyNewListings: false,
  weeklyUpdate: false,
  dailyNetwork: false,
  dailyNeeds: false,
  types: {
    freehold: false,
    condo: false,
    commercial: false,
    land: false,
    income: false,
    lease: false,
  },
  officeMessages: {
    appointmentUpdates: {
      email: false,
      text: false,
    },
    frontDesk: {
      email: false,
      text: false,
    },
    bookShowingDefault: {
      email: false,
      text: false,
      office: false,
    },
  },
  disabled_locations: [],
};

export type NotificationSettingsProps = {
  auth?: AuthContextType['auth'];
  fetchAuth?: AuthContextType['fetchAuth'];
  mode: 'user' | 'org' | 'unsubscribe';
};

const NotificationSettings: React.FC<NotificationSettingsProps> = ({
  auth,
  fetchAuth,
  mode = 'user',
}) => {
  const routeParams = useParams<{
    orgId?: string;
    location?: string;
    unsubscribeId?: string;
  }>();

  const [messageApi, messageContextHolder] = message.useMessage();

  const { md } = useBreakpoint();

  const [loading, setLoading] = useState(false);

  const [currentNotifications, setCurrentNotifications] = React.useState(
    DEFAULT_NOTIFICATIONS,
  );

  const [modifiedNotifications, setModifiedNotifications] = React.useState(
    DEFAULT_NOTIFICATIONS,
  );

  const isModified = useMemo(
    () => !isEqual(currentNotifications, modifiedNotifications),
    [currentNotifications, modifiedNotifications],
  );

  const [locations, setLocations] = React.useState<LocationsDTO['locations']>(
    [],
  );

  // For unsubscribe mode only
  const [userName, setUserName] = React.useState<string>();
  const [isLicensed, setIsLicensed] = React.useState<boolean>();
  const [hasSmsFeature, setHasSmsFeature] = React.useState<boolean>();

  const handleChange = (changes: Partial<typeof modifiedNotifications>) => {
    setModifiedNotifications(prev => ({ ...prev, ...changes }));
  };

  const fetchUserNotifications = async () => {
    const fetchDataByMode = async () => {
      let apiNotifications: INotificationSettings;
      let apiLocations: LocationsDTO['locations'];
      let apiUserName: string | undefined;
      let apiIsLicensed: boolean | undefined;
      let apiHasSmsFeature: boolean | undefined;

      if (mode === 'unsubscribe') {
        const { unsubscribeId } = routeParams;

        if (!unsubscribeId)
          throw new Error('Missing route params: unsubscribeId');

        const data =
          await getNotificationSettingsByUnsubscribeId(unsubscribeId);

        apiNotifications = data.notifications;
        apiLocations = data.locations;
        apiUserName = `${data.firstName} ${data.lastName}`;
        apiIsLicensed = data.isLicensed;
        apiHasSmsFeature = data.hasSmsFeature;
      } else {
        const [data1, data2] = await Promise.all([
          getNotificationSettings(),
          getLocations(),
        ]);

        apiNotifications = data1;
        apiLocations = data2.locations;
      }

      return {
        apiNotifications,
        apiLocations,
        apiUserName,
        apiIsLicensed,
        apiHasSmsFeature,
      };
    };

    setLoading(true);

    try {
      const {
        apiNotifications,
        apiLocations,
        apiUserName,
        apiIsLicensed,
        apiHasSmsFeature,
      } = await fetchDataByMode();

      setCurrentNotifications(apiNotifications);
      setModifiedNotifications(apiNotifications);
      setLocations(apiLocations);
      setUserName(apiUserName);
      setIsLicensed(apiIsLicensed);
      setHasSmsFeature(apiHasSmsFeature);
    } catch (err) {
      errorReporter.error(err);
      messageApi.error({ content: 'Error fetching notifications.' });
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchUserNotifications();
  }, [auth]);

  const onSave = async () => {
    const saveDataByMode = async () => {
      if (mode === 'unsubscribe') {
        const { unsubscribeId } = routeParams;

        if (!unsubscribeId)
          throw new Error('Missing route params: unsubscribeId');

        await saveNotificationSettingsByUnsubscribeId(
          unsubscribeId,
          modifiedNotifications,
        );
      } else {
        await saveNotificationSettings(modifiedNotifications);
      }
    };

    if (loading || !isModified) return;

    setLoading(true);
    try {
      await saveDataByMode();
    } catch (err) {
      messageApi.error({ content: 'Error saving notifications.' });
      errorReporter.error(err);
      return;
    }

    messageApi.success({ content: 'Notifications have been updated' });

    if (mode === 'user' && fetchAuth) await fetchAuth();
    await fetchUserNotifications();
  };

  return (
    <>
      {messageContextHolder}
      <StyledSpin spinning={loading}>
        <NotificationSettingsPageContainer $md={md}>
          {mode === 'unsubscribe' && (
            <StyledAlert
              type="warning"
              showIcon
              message={
                <>
                  You are updating notification settings on behalf of{' '}
                  <b>{userName}</b>
                </>
              }
            />
          )}

          <ShowingUpdatesCard
            handleChange={handleChange}
            notifications={modifiedNotifications}
            setNotifications={setModifiedNotifications}
            isLicensed={isLicensed}
            hasSmsFeature={hasSmsFeature}
          />

          <ChatMessagesCard
            handleChange={handleChange}
            modifiedNotifications={modifiedNotifications}
          />

          <NotificationsCard
            notifications={modifiedNotifications}
            locations={locations}
            setModifiedNotifications={setModifiedNotifications}
          />

          <ActionBar
            floating={md}
            visible={isModified}
            onReset={() => setModifiedNotifications(currentNotifications)}
            onSave={onSave}
          />
        </NotificationSettingsPageContainer>
      </StyledSpin>
    </>
  );
};

const StyledAlert = styled(Alert)`
  width: 100%;
`;

// Remove weird margin-left
const StyledSpin = styled(Spin)`
  > * {
    margin-left: 0;
  }
`;

const NotificationSettingsPageContainer = styled.div<{ $md: boolean }>`
  padding-top: 32px;
  padding-bottom: ${({ $md }) => ($md ? '112' : '32')}px;
  padding-left: ${({ $md }) => ($md ? '10%' : '0%')};
  padding-right: ${({ $md }) => ($md ? '10%' : '0%')};
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
`;

export default NotificationSettings;
